import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import type * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import type { SortDirection } from "@/hooks/use-data-grid";
import { cn } from "@/lib/utils";

/** Every body row. Hover is a fill, never a border change, so columns stay still. */
export const ROW_CLASS = "border-b border-border last:border-0 transition-colors hover:bg-muted/60";
/** A totals row: heavier rule above it, soft fill, semibold. */
export const TOTAL_ROW_CLASS = "border-t-2 border-border bg-muted/40 font-semibold";

interface DataTableHeader {
  label: string;
  className?: string;
  /**
   * When set, the header becomes a sort toggle. Must match a server `sort`
   * value (i.e. a key in the endpoint's sort-column allowlist). Requires the
   * `sort`/`sortDir`/`onSort` props to be passed.
   */
  sortKey?: string;
  /**
   * How this column is calculated, in one sentence.
   *
   * Every figure here is derived -- margin is recomputed from summed revenue
   * and cost, "value at rate" prices billable hours only, "below target" is
   * dollars not points. A reader who guesses wrong acts on the wrong number,
   * so the derivation travels with the column rather than living in someone's
   * head.
   */
  help?: string;
}

interface DataTableProps {
  headers: DataTableHeader[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  colSpan?: number;
  className?: string;
  /** Inside a flush Card: no border or radius of its own, 20px gutters. */
  flush?: boolean;
  /** 16px gutters on a flush table with eight or more columns, so it fits at 1440. */
  dense?: boolean;
  /** Active sort key — wire from `useDataGrid().sort`. */
  sort?: string;
  /** Active sort direction — wire from `useDataGrid().sortDir`. */
  sortDir?: SortDirection;
  /** Sort handler — wire from `useDataGrid().toggleSort`. */
  onSort?: (key: string) => void;
}

export function DataTable({
  headers,
  children,
  loading = false,
  empty,
  colSpan,
  className,
  flush = false,
  dense = false,
  sort,
  sortDir,
  onSort,
}: DataTableProps) {
  const span = colSpan ?? headers.length;
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : Boolean(children);
  const showEmpty = !loading && !hasChildren && empty;

  return (
    <div className={cn("overflow-x-auto", flush ? "" : "rounded-lg border", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            {headers.map((header) => {
              const sortKey = header.sortKey;
              const active = sortKey !== undefined && sort === sortKey;
              return (
                <th
                  key={header.label || header.sortKey}
                  aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                  className={cn(
                    "py-3 text-left font-display text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground",
                    flush && !dense ? "px-5" : "px-4",
                    header.className,
                  )}
                >
                  {sortKey !== undefined && onSort !== undefined ? (
                    <button
                      type="button"
                      onClick={() => onSort(sortKey)}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      // The help sits on the same element as the label so it is
                      // reachable by keyboard and by hover alike; a separate
                      // icon-only target would be neither.
                      title={header.help}
                    >
                      {header.label}
                      {header.help ? <Info className="size-3 opacity-40" aria-hidden /> : null}
                      {active ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3 opacity-50" />
                      )}
                    </button>
                  ) : header.help ? (
                    <span className="inline-flex items-center gap-1" title={header.help}>
                      {header.label}
                      <Info className="size-3 opacity-40" aria-hidden />
                    </span>
                  ) : (
                    header.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="border-b last:border-b-0">
                {headers.map((_header, colIndex) => (
                  <td
                    key={`skeleton-${rowIndex}-${colIndex}`}
                    className={flush && !dense ? "px-5 py-3" : "px-4 py-3"}
                  >
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}
